// Types for Lifestyle Benefits Analytics Dashboard

export enum TimeRangeFilter {
  LAST_7_DAYS = '7d',
  LAST_30_DAYS = '30d',
  YEAR_TO_DATE = 'ytd',
  LAST_12_MONTHS = '1y'
}

export interface LSAMetrics {
  totalReimbursed: number;
  avgPerMember: number;
  participationRate: number;
  budgetUtilization: number;
  totalMembers: number;
  activeMembers: number;
}

export interface CategorySpending {
  category: string;
  amount: number;
}

export interface SpendingTrend {
  date: string;
  amount: number;
}

export interface PendingTransaction {
  id: string;
  memberName: string;
  amount: number;
  category: string;
  submittedDate: string;
  receiptUrl?: string;
  status: 'pending' | 'approved' | 'rejected';
  companyName: string;
}

export interface BudgetUtilization {
  totalBudget: number;
  usedBudget: number;
  remainingBudget: number;
  utilizationPercentage: number;
  recentCompletedTransactions: Array<{
    id: string;
    memberName: string;
    amount: number;
    companyName: string;
    completedDate: string;
  }>;
}

export interface EngagementInsights {
  mostActiveMembers: Array<{
    userId: string;
    totalAmount: number;
  }>;
  mostPopularCategories: Array<{
    category: string;
    amount: number;
  }>;
  avgReimbursementAmount: number;
}

export interface LifestyleGroup {
  id: string;
  name: string;
  memberCount?: number;
  totalBudget?: number;
  usedBudget?: number;
  status: 'active' | 'paused' | 'deleted';
}

export interface ExportData {
  summary: LSAMetrics;
  categories: CategorySpending[];
  transactions: PendingTransaction[];
  trends: SpendingTrend[];
}

// GraphQL Analytics Types
export interface LSATotalSavedAmount {
  coveredAmount: number;
  costAmount: number;
}

export interface LSACategoryBreakdown {
  categoryId: string;
  category?: string; // For backward compatibility
  amount: number;
  percentage?: number;
  transactionCount?: number;
}

export interface LSATopUser {
  userId: string;
  name?: string; // For backward compatibility
  totalSaved: number;
  transactionCount?: number;
}

export interface LSADailySavings {
  date: string;
  amount: number;
}

export interface LSAWeeklySavings {
  weekStart: string;
  amount: number;
}

export interface LSAMonthlySavings {
  month: string;
  amount: number;
}

export interface LSATransactionSummary {
  transactionId: string;
  id?: string; // For backward compatibility
  userId: string;
  firstName?: string;
  lastName?: string;
  memberName?: string; // For backward compatibility
  amount: number;
  categoryId?: string;
  category?: string; // For backward compatibility
  date?: string;
  submittedDate?: string; // For backward compatibility
  brandName?: string;
  status: string;
}

export interface LifestylePeriodAnalytics {
  totalSaved: LSATotalSavedAmount;
  averageSavedPerMember: LSATotalSavedAmount;
  participationRate: number;
  categoryBreakdown: LSACategoryBreakdown[];
  topCategories: LSACategoryBreakdown[];
  topUsers: LSATopUser[];
  dailySavings: LSADailySavings[];
  weeklySavings: LSAWeeklySavings[];
  monthlySavings: LSAMonthlySavings[];
  pendingTransactions: LSATransactionSummary[];
  completedTransactions: LSATransactionSummary[];
  totalBudgetRemaining: number;
  totalSpent: number;
}

export interface LifestyleAnalyticsResponse {
  sevenDays: LifestylePeriodAnalytics;
  thirtyDays: LifestylePeriodAnalytics;
  yearToDate: LifestylePeriodAnalytics;
  oneYear: LifestylePeriodAnalytics;
  statusBreakdown: {
    completed: LifestylePeriodAnalytics;
    pending: LifestylePeriodAnalytics;
    rejected: LifestylePeriodAnalytics;
    all: LifestylePeriodAnalytics;
  };
}
